import { AlertCircleIcon, Loader2Icon } from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from '@/lib/shadcn/utils';

export type ProviderMarkStatus = 'pending' | 'failed' | 'idle';

interface ProviderMarkProps {
  /** The provider's name, e.g. "Slack": the mark's label and status text. */
  provider: string;
  /** The provider's own logo, so the screen shows the method the user picked. */
  logo: ReactNode;
  /** A spinner while signing in, an alert once it failed, nothing otherwise. */
  status: ProviderMarkStatus;
}

/**
 * The sign-in provider's logo on an OAuth callback screen, badged with its
 * status. The badge is keyed by status, so a change (spinner to alert) mounts
 * a new one that scales in from 90% rather than swapping in place.
 */
export const ProviderMark = ({ provider, logo, status }: ProviderMarkProps) => (
  <div className="relative mb-5 size-14">
    <div
      role="img"
      aria-label={provider}
      className="flex size-full items-center justify-center rounded-2xl border bg-white shadow-xs"
    >
      {logo}
    </div>
    {status !== 'idle' && (
      <span
        key={status}
        className={cn(
          'absolute -right-2 -bottom-2 flex size-6 items-center justify-center rounded-full ring-2 ring-white',
          'transition-[opacity,scale] duration-150 ease-(--ease-out-strong) starting:scale-90 starting:opacity-0 motion-reduce:starting:scale-100',
          status === 'failed'
            ? 'bg-destructive text-white'
            : 'bg-background border',
        )}
      >
        {status === 'failed' ? (
          <AlertCircleIcon className="size-4" aria-hidden="true" />
        ) : (
          // Faster than Tailwind's 1s spin: a quicker spinner reads as a
          // shorter wait. Reduced motion pulses instead, so it never looks frozen.
          <Loader2Icon
            className="text-foreground/70 size-3.5 motion-safe:animate-[spin_750ms_linear_infinite] motion-reduce:animate-pulse"
            aria-hidden="true"
          />
        )}
      </span>
    )}
    {status === 'pending' && (
      <span role="status" className="sr-only">
        Signing you in with {provider}
      </span>
    )}
  </div>
);
